import type { StaffEmployeeRow } from "@/lib/staff-api";
import type { SortDir } from "@/lib/list-url";
import { badgeExpiry, formatCalendarDate, STATUS_LABELS } from "@/lib/roster";
import { formatDate, formatDaysAgo } from "@/lib/formatters";
import { CompanyLink } from "./CompanyLink";
import { EmployeeLink } from "./EmployeeLink";
import { MoreActions } from "./QueueRow";
import { SortableTable, type Column } from "./SortableTable";

const MARKER = "ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700";
const HIDDEN_TAG = "ml-2 rounded-full bg-gray-700 px-2 py-0.5 text-xs font-medium text-white";

interface EmployeesTableProps {
  caption: string;
  rows: StaffEmployeeRow[];
  sort: string;
  dir: SortDir;
  onSort: (key: string) => void;
  /** Off on a company's own page, where every row is that company. */
  showCompany?: boolean;
  /** Mark and IT get "More actions"; Accounting reads only. */
  canAct: boolean;
}

function expiryOf(row: StaffEmployeeRow): string {
  const expiry = badgeExpiry(row.clearanceDate);
  return expiry ? formatCalendarDate(expiry) : "—";
}

/** The employee lookup table: the Employees tab, a company's page, and search. */
export function EmployeesTable({ caption, rows, sort, dir, onSort, showCompany = true, canAct }: EmployeesTableProps): React.ReactElement {
  const columns: Column<StaffEmployeeRow>[] = [
    {
      key: "name",
      label: "Employee",
      render: (r) => (
        <span>
          <EmployeeLink employee={r} />
          {r.inactive && <span className={MARKER}>No longer employed</span>}
        </span>
      ),
    },
    ...(showCompany
      ? [
          {
            key: "company",
            label: "Company",
            render: (r: StaffEmployeeRow) => (
              <span>
                <CompanyLink vendorId={r.vendorId} vendor={r.vendor} />
                {r.vendorHidden && <span className={HIDDEN_TAG}>Hidden</span>}
              </span>
            ),
          },
        ]
      : []),
    { key: "stage", label: "Stage", render: (r) => STATUS_LABELS[r.status] },
    {
      key: "changed",
      label: "Last change",
      render: (r) => (
        <span>
          {formatDate(r.updatedAt)} <span className="text-gray-600">· {formatDaysAgo(r.updatedAt)}</span>
        </span>
      ),
    },
    { key: "expires", label: "Badge expires", render: expiryOf },
    ...(canAct
      ? [{ key: "actions", label: "Actions", sortable: false, hideLabel: true, align: "right" as const, render: (r: StaffEmployeeRow) => <MoreActions employee={r} /> }]
      : []),
  ];
  return <SortableTable caption={caption} columns={columns} rows={rows} rowKey={(r) => r.id} sort={sort} dir={dir} onSort={onSort} />;
}
